/**
 * Template de prise de besoin : Politique de cybersécurité v2
 * Aligné sur NIST CSF 2.0, ITIL 4, COBIT 2019 et ISO 27001
 * Version complète couvrant tous les aspects d'une politique de cybersécurité
 */

import type { IntakeTemplate } from '../templateTypes';

export const templatePolitiqueCybersecuriteV2: IntakeTemplate = {
  id: 'politique-cybersecurite-v2',
  name: 'Prise de besoin Politique de cybersécurité v2',
  description:
    'Questionnaire complet pour élaborer une politique de cybersécurité alignée sur NIST CSF 2.0, ITIL 4, COBIT 2019 et ISO 27001. Couvre : utilisations autorisées, logiciels, médias sociaux, BYOD, contrôles d\'accès, authentification, mots de passe, courriel, restrictions et IA.',
  documentTypeCode: 'POLITIQUE_CYBER',
  version: '2.0.0',
  phases: [
    {
      id: 'phase1',
      title: 'Qualification & Contexte',
      description: 'Informations sur l\'entreprise et utilisations autorisées',
      icon: 'Building2',
      questions: [
        {
          id: 'entreprise',
          label: 'Entreprise',
          type: 'text',
          placeholder: 'Nom de l\'entreprise',
          helpText: 'Identifiant principal pour la politique de cybersécurité. Cette information est essentielle pour personnaliser la politique selon le contexte organisationnel.',
        },
        {
          id: 'enquete_securite_utilisateurs',
          label: 'Exigez-vous que les usagers des actifs TI soient soumis à une enquête de sécurité ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'Selon NIST CSF 2.0 (PR.AC-1), les organisations doivent identifier et authentifier les utilisateurs. Une enquête de sécurité fait partie des meilleures pratiques pour évaluer la fiabilité des utilisateurs ayant accès aux systèmes critiques.',
        },
        {
          id: 'formation_obligatoire_utilisateurs',
          label: 'Exigez-vous que les usagers des actifs TI réussissent une formation obligatoire ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'NIST CSF 2.0 (PR.AT) et ISO 27001 (A.7.2.2) exigent la sensibilisation et la formation en sécurité. ITIL 4 recommande des programmes de formation continue pour maintenir la compétence des utilisateurs.',
        },
        {
          id: 'groupes_utilisateurs_uniformes',
          label: 'Est-ce que tous les employés seront soumis aux mêmes mesures de sécurité, ou devrions-nous créer différents groupes ?',
          type: 'select',
          options: ['Oui - Tous les mêmes mesures', 'Non - Groupes d\'utilisateurs différenciés'],
          helpText: 'COBIT 2019 (APO07.02) et NIST CSF 2.0 (PR.AC-4) recommandent une gestion des accès basée sur les rôles (RBAC). La différenciation par groupes permet une sécurité adaptée au niveau de risque de chaque catégorie d\'utilisateurs.',
        },
        {
          id: 'groupes_utilisateurs_detail',
          label: 'Quels sont les différents groupes d\'utilisateurs ?',
          type: 'textarea',
          placeholder: 'Ex: Direction, Gestionnaires, Employés standards, Consultants externes, Administrateurs TI...',
          helpText: 'Définir clairement les groupes permet d\'appliquer des politiques de sécurité granulaires. Chaque groupe devrait avoir des droits d\'accès et des restrictions adaptés à son rôle (principe du moindre privilège - NIST CSF 2.0 PR.AC-3).',
        },
        {
          id: 'ton_document_politique',
          label: 'Quel ton souhaitez-vous pour cette politique de cybersécurité ?',
          type: 'select',
          options: [
            'Formel et juridique (ton impératif, sanctions claires)',
            'Pédagogique et collaboratif (ton explicatif, guide pratique)',
            'Mixte (équilibre entre formel et pédagogique)',
          ],
          helpText: 'Le ton influence la réception et l\'adhésion des utilisateurs. Un ton formel assure la conformité légale, tandis qu\'un ton pédagogique favorise la compréhension et l\'adoption volontaire. Le mixte combine les avantages des deux approches.',
        },
        {
          id: 'plan_formation_sensibilisation',
          label: 'Avez-vous un plan de formation et sensibilisation en cybersécurité pour vos employés ?',
          type: 'select',
          options: ['Oui, plan structuré existant', 'Oui, plan à développer', 'Non, pas encore'],
          helpText: 'NIST CSF 2.0 (PR.AT) et ISO 27001 (A.7.2.2) exigent des programmes de sensibilisation et de formation. ITIL 4 recommande des formations continues pour maintenir la compétence et la vigilance des utilisateurs.',
        },
        {
          id: 'frequence_formation_securite',
          label: 'À quelle fréquence souhaitez-vous que les formations en sécurité soient dispensées ?',
          type: 'select',
          options: ['Annuelle', 'Semestrielle', 'Trimestrielle', 'Mensuelle', 'À déterminer'],
          helpText: 'La fréquence des formations doit être adaptée au niveau de risque et à la culture organisationnelle. Les environnements à haut risque nécessitent des formations plus fréquentes.',
        },
        {
          id: 'gestion_violations_politique',
          label: 'Comment souhaitez-vous gérer les violations de cette politique de cybersécurité ?',
          type: 'select',
          options: [
            'Sanctions disciplinaires progressives (avertissement, suspension, renvoi)',
            'Approche éducative avec correction',
            'Mixte (éducatif pour erreurs, disciplinaire pour violations intentionnelles)',
          ],
          helpText: 'NIST CSF 2.0 (RS.CO) et ISO 27001 (A.7.2.3) recommandent des procédures claires pour gérer les violations. Une approche équilibrée favorise la conformité tout en maintenant un environnement de travail positif.',
        },
        {
          id: 'procedure_escalade_incidents',
          label: 'Quelle procédure d\'escalade souhaitez-vous pour les incidents de sécurité liés à la violation de la politique ?',
          type: 'textarea',
          placeholder: 'Ex: Signalement immédiat au gestionnaire, puis à TI, puis à la direction selon la gravité...',
          helpText: 'ITIL 4 (Incident Management) et NIST CSF 2.0 (RS.RP) recommandent des procédures d\'escalade claires pour assurer une réponse rapide et appropriée aux incidents de sécurité.',
        },
      ],
    },
    {
      id: 'phase2',
      title: 'Contrôles d\'Accès & Utilisation',
      description: 'Logiciels autorisés, médias sociaux, BYOD et utilisation personnelle',
      icon: 'Shield',
      questions: [
        {
          id: 'admin_postes_travail',
          label: 'Permettez-vous à vos employés d\'être administrateurs de leurs postes de travail ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'NIST CSF 2.0 (PR.AC-3) et ISO 27001 (A.9.2.3) recommandent le principe du moindre privilège. Les comptes administrateurs locaux augmentent significativement les risques de sécurité. COBIT 2019 (DSS05.04) suggère de limiter les privilèges administratifs.',
        },
        {
          id: 'telechargement_tous_logiciels',
          label: 'Permettez-vous le téléchargement/l\'utilisation de tous les logiciels ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'ISO 27001 (A.12.5.1) exige un contrôle des logiciels installés. ITIL 4 (Service Configuration Management) recommande un catalogue de logiciels autorisés pour réduire les risques de sécurité et maintenir la conformité des licences.',
        },
        {
          id: 'utilisation_tous_sites_web',
          label: 'Permettez-vous l\'utilisation de tous les sites Web ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'NIST CSF 2.0 (PR.DS-1) recommande la protection des données en transit. La restriction de sites web permet de réduire l\'exposition aux menaces (phishing, malware) et de contrôler la bande passante.',
        },
        {
          id: 'sites_web_autorises_interdits',
          label: 'Si non, lesquels autorisez-vous OU lesquels défendez-vous ?',
          type: 'textarea',
          placeholder: 'Ex: Autoriser: SharePoint, Teams, Office 365 / Interdire: Sites de téléchargement, réseaux sociaux personnels...',
          helpText: 'Une liste claire de sites autorisés/interdits facilite la mise en œuvre technique (filtrage web) et la communication aux utilisateurs. Aligné sur NIST CSF 2.0 (PR.DS-1) pour la protection des données.',
        },
        {
          id: 'methode_logiciels_autorises',
          label: 'Méthode de mise en place - Logiciels Autorisés',
          type: 'select',
          options: ['Confiance envers les employés', 'Restriction technologique'],
          helpText: 'ITIL 4 recommande une approche équilibrée : formation et sensibilisation (confiance) combinées à des contrôles techniques (restriction). COBIT 2019 (DSS05.04) privilégie les contrôles automatisés pour réduire les risques humains.',
        },
        {
          id: 'moyens_restriction_logiciels',
          label: 'Quels moyens de restriction technologique allez-vous utiliser ? - Logiciels Autorisés',
          type: 'textarea',
          placeholder: 'Ex: AppLocker, Group Policy, Software Restriction Policies, solutions EDR...',
          helpText: 'Les solutions techniques de restriction (AppLocker, GPO, EDR) permettent d\'appliquer automatiquement les politiques. NIST CSF 2.0 (PR.DS-1) et ISO 27001 (A.12.5.1) recommandent des contrôles techniques pour l\'installation de logiciels.',
        },
        {
          id: 'notes_logiciels_autorises',
          label: 'Notes - Logiciels Autorisés',
          type: 'textarea',
          placeholder: 'Informations additionnelles sur la gestion des logiciels autorisés...',
        },
        {
          id: 'demande_application_non_autorisee',
          label: 'S\'il y a un employé qui souhaite utiliser une application n\'étant pas autorisée au préalable, de quelle manière ferait-il la demande ?',
          type: 'textarea',
          placeholder: 'Ex: Formulaire de demande, approbation par le gestionnaire, évaluation par TI...',
          helpText: 'ITIL 4 (Request Fulfillment) recommande un processus structuré pour les demandes d\'exception. COBIT 2019 (APO12.05) suggère un workflow d\'approbation pour maintenir la traçabilité et la gouvernance.',
        },
        {
          id: 'methode_logiciels_non_autorises',
          label: 'Méthode de mise en place - Utilisation de logiciels, applications et sites Web non-autorisés',
          type: 'select',
          options: ['Confiance envers les employés', 'Restriction technologique'],
          helpText: 'Même principe que pour les logiciels autorisés. Une combinaison des deux approches est généralement recommandée par NIST CSF 2.0 et ITIL 4.',
        },
        {
          id: 'moyens_restriction_logiciels_non_autorises',
          label: 'Quels moyens de restriction technologique allez-vous utiliser ? - Utilisation de logiciels non-autorisés',
          type: 'textarea',
          placeholder: 'Ex: DLP (Data Loss Prevention), monitoring des applications, alertes automatiques...',
          helpText: 'Les solutions DLP et de monitoring permettent de détecter et bloquer l\'utilisation non autorisée. NIST CSF 2.0 (DE.CM-1) recommande la surveillance continue des activités système.',
        },
        {
          id: 'notes_logiciels_non_autorises',
          label: 'Notes - Utilisation de logiciels non-autorisés',
          type: 'textarea',
        },
        {
          id: 'medias_sociaux_fonctions_officielles',
          label: 'Autorisez-vous l\'utilisation des médias sociaux au travail pour une utilisation liée aux activités opérationnelles des employés ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'Les médias sociaux peuvent être des outils de communication professionnelle légitimes, mais nécessitent des politiques claires pour éviter les risques de réputation et de sécurité. ISO 27001 (A.7.3.1) couvre l\'utilisation acceptable des actifs.',
        },
        {
          id: 'methode_medias_sociaux_officiels',
          label: 'Méthode de mise en place - Utilisation des médias sociaux dans le cadre de fonctions officielles',
          type: 'select',
          options: ['Confiance envers les employés', 'Restriction technologique'],
        },
        {
          id: 'moyens_restriction_medias_sociaux_officiels',
          label: 'Quels moyens de restriction technologique allez-vous utiliser ? - Utilisation des médias sociaux fonctions officielles',
          type: 'textarea',
          placeholder: 'Ex: Filtrage web, solutions de sécurité web gateway, monitoring...',
          helpText: 'NIST CSF 2.0 (PR.DS-1) recommande la protection des données en transit, y compris sur les plateformes de médias sociaux.',
        },
        {
          id: 'mots_passe_identiques_reseaux_sociaux',
          label: 'Permettez-vous que les employés utilisent les mêmes mots de passe pour leurs réseaux sociaux personnels et professionnels ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'NIST SP 800-63B et ISO 27001 (A.9.4.3) interdisent la réutilisation de mots de passe entre systèmes. Cette pratique augmente significativement les risques en cas de compromission d\'un compte externe.',
        },
        {
          id: 'notes_medias_sociaux_officiels',
          label: 'Notes - Utilisation des médias sociaux fonctions officielles',
          type: 'textarea',
        },
        {
          id: 'medias_sociaux_utilisation_personnelle',
          label: 'Autorisez-vous l\'utilisation des médias sociaux au travail ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'ITIL 4 et COBIT 2019 recommandent une politique d\'utilisation acceptable claire. L\'utilisation personnelle peut être autorisée avec des restrictions (pauses, heures permises) pour maintenir la productivité.',
        },
        {
          id: 'moments_medias_sociaux_personnels',
          label: 'Si oui, à quels moments ?',
          type: 'textarea',
          placeholder: 'Ex: Pauses, heures de dîner, après les heures de travail...',
        },
        {
          id: 'methode_medias_sociaux_personnels',
          label: 'Méthode de mise en place - Lignes directrices pour l\'utilisation personnelle des médias sociaux',
          type: 'select',
          options: ['Confiance envers les employés', 'Restriction technologique'],
        },
        {
          id: 'moyens_restriction_medias_sociaux_personnels',
          label: 'Quels moyens de restriction technologique allez-vous utiliser ? - Utilisation personnelle des médias sociaux',
          type: 'textarea',
          placeholder: 'Ex: Filtrage par horaire, limitation de bande passante, monitoring...',
        },
        {
          id: 'notes_medias_sociaux_personnels',
          label: 'Notes - Utilisation personnelle des médias sociaux',
          type: 'textarea',
        },
        {
          id: 'actifs_ti_fins_personnelles',
          label: 'Permettez-vous l\'utilisation des actifs TI à des fins personnelles ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'ISO 27001 (A.7.3.1) exige une politique d\'utilisation acceptable des actifs. Une utilisation personnelle limitée peut être autorisée, mais doit être clairement définie pour éviter les abus et les risques de sécurité.',
        },
        {
          id: 'actifs_ti_fins_personnelles_detail',
          label: 'Si oui, lesquels ?',
          type: 'text',
          placeholder: 'Ex: Ordinateur portable, téléphone, imprimante...',
        },
        {
          id: 'ordinateurs_personnels_acces',
          label: 'Permettez-vous l\'utilisation d\'ordinateurs personnels afin d\'accéder à des actifs TI de l\'entreprise ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'BYOD (Bring Your Own Device) nécessite des politiques strictes selon NIST CSF 2.0 (PR.DS-2) et ISO 27001 (A.6.2.1). Les risques incluent la perte de contrôle sur les appareils, la gestion des correctifs et la séparation des données personnelles/professionnelles.',
        },
        {
          id: 'methode_ordinateurs_personnels',
          label: 'Méthode de mise en place - Usage d\'ordinateurs personnels à des fins professionnelles',
          type: 'select',
          options: ['Confiance envers les employés', 'Restriction technologique'],
        },
        {
          id: 'moyens_restriction_ordinateurs_personnels',
          label: 'Quels moyens de restriction technologique allez-vous utiliser ? - Usage d\'ordinateurs personnels',
          type: 'textarea',
          placeholder: 'Ex: MDM (Mobile Device Management), VPN, solutions de conteneurisation, accès web uniquement...',
          helpText: 'Les solutions MDM et de conteneurisation permettent de séparer les environnements personnels et professionnels. NIST CSF 2.0 (PR.DS-2) recommande des contrôles techniques pour les appareils BYOD.',
        },
        {
          id: 'notes_ordinateurs_personnels',
          label: 'Notes - Usage d\'ordinateurs personnels',
          type: 'textarea',
        },
        {
          id: 'appareils_mobiles_personnels_acces',
          label: 'Permettez-vous l\'utilisation d\'appareils mobiles personnels afin d\'accéder à des actifs TI de l\'entreprise ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'Les appareils mobiles personnels (BYOD mobile) présentent des risques similaires aux ordinateurs personnels, avec des défis additionnels liés à la mobilité et à la perte/vol. NIST CSF 2.0 (PR.DS-2) et ISO 27001 (A.6.2.1) couvrent la gestion des appareils mobiles.',
        },
        {
          id: 'methode_appareils_mobiles',
          label: 'Méthode de mise en place - Usage d\'appareils mobiles personnels à des fins professionnelles',
          type: 'select',
          options: ['Confiance envers les employés', 'Restriction technologique'],
        },
        {
          id: 'moyens_restriction_appareils_mobiles',
          label: 'Quels moyens de restriction technologique allez-vous utiliser ? - Usage d\'appareils mobiles personnels',
          type: 'textarea',
          placeholder: 'Ex: MDM mobile, MAM (Mobile Application Management), conteneurisation, applications sécurisées...',
          helpText: 'Les solutions MDM/MAM permettent de gérer et sécuriser les appareils mobiles. NIST CSF 2.0 (PR.DS-2) recommande l\'application de politiques de sécurité sur tous les appareils accédant aux ressources organisationnelles.',
        },
        {
          id: 'telechargement_fichiers_appareils_non_geres',
          label: 'Permettez-vous le téléchargement de fichiers sur des appareils qui ne sont pas gérés par l\'entreprise (cellulaire et/ou portable) ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'NIST CSF 2.0 (PR.DS-1) et ISO 27001 (A.8.2.1) recommandent de contrôler le transfert de données vers des appareils non gérés pour prévenir la perte de données. Les solutions DLP peuvent bloquer ou monitorer ces transferts.',
        },
        {
          id: 'methode_telechargement_appareils_non_geres',
          label: 'Méthode de mise en place - Téléchargement de fichiers sur des appareils non gérés',
          type: 'select',
          options: ['Confiance envers les employés', 'Restriction technologique'],
        },
        {
          id: 'moyens_restriction_telechargement_non_geres',
          label: 'Quels moyens de restriction technologique allez-vous utiliser ? - Téléchargement fichiers appareils non gérés',
          type: 'textarea',
          placeholder: 'Ex: DLP, blocage des ports USB, solutions de conteneurisation, accès web uniquement...',
          helpText: 'Les solutions DLP (Data Loss Prevention) permettent de détecter et bloquer les transferts non autorisés. NIST CSF 2.0 (PR.DS-1) et COBIT 2019 (DSS05.07) recommandent des contrôles pour prévenir la perte de données.',
        },
        {
          id: 'notes_appareils_mobiles',
          label: 'Notes - Usage d\'appareils mobiles personnels',
          type: 'textarea',
        },
      ],
    },
    {
      id: 'phase3',
      title: 'Authentification & Sécurité',
      description: 'Contrôles d\'accès, authentification, mots de passe et 2FA',
      icon: 'Key',
      questions: [
        {
          id: 'pim_comptes_privilegies',
          label: 'Exigez-vous l\'utilisation d\'un PIM (Privileged Identity Management) pour tous les utilisateurs avec des accès privilégiés ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'NIST CSF 2.0 (PR.AC-3) et ISO 27001 (A.9.2.3) exigent la gestion des comptes privilégiés. Les solutions PIM/PAM (Privileged Access Management) permettent de contrôler, monitorer et auditer l\'accès aux systèmes critiques. COBIT 2019 (DSS05.04) recommande fortement cette pratique.',
        },
        {
          id: 'pim_microsoft_ou_autre',
          label: 'Si oui, seulement pour Microsoft ou voulez-vous une autre solution technologique, tel qu\'un FortiPAM ?',
          type: 'select',
          options: ['Seulement pour Microsoft', 'Autre solution technologique (ex: FortiPAM, CyberArk, BeyondTrust)'],
          helpText: 'Les solutions PAM peuvent être intégrées à l\'écosystème Microsoft (Azure PIM) ou être des solutions tierces dédiées. Le choix dépend de l\'infrastructure existante et des besoins spécifiques. NIST CSF 2.0 (PR.AC-3) ne prescrit pas de solution particulière.',
        },
        {
          id: 'notes_pim',
          label: 'Notes - Comptes à accès privilégiés',
          type: 'textarea',
        },
        {
          id: 'restriction_locaux_physiques_roles',
          label: 'Avez-vous une restriction aux locaux physiques selon les rôles des employés ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'ISO 27001 (A.11.1.1) et NIST CSF 2.0 (PR.AC-1) exigent des contrôles d\'accès physiques. La restriction par rôles (badges, zones sécurisées) fait partie des meilleures pratiques pour protéger les actifs critiques.',
        },
        {
          id: 'notes_controles_acces_physiques',
          label: 'Notes - Contrôles d\'accès physiques',
          type: 'textarea',
        },
        {
          id: 'acces_exterieur_canada',
          label: 'Voulez-vous autoriser l\'accès aux actifs TI de l\'entreprise à l\'extérieur du Canada en tout temps, uniquement lorsqu\'un employé quitte en voyage et a besoin d\'y avoir accès ou jamais ?',
          type: 'select',
          options: ['En tout temps', 'Uniquement lorsqu\'un employé est en voyage', 'Jamais'],
          helpText: 'La restriction géographique peut être nécessaire pour la conformité (ex: Loi 25, RGPD) et la sécurité. NIST CSF 2.0 (PR.DS-1) recommande de contrôler l\'accès aux données selon la localisation. COBIT 2019 (APO13.02) couvre la gestion des risques géographiques.',
        },
        {
          id: 'notes_acces_distants',
          label: 'Notes - Accès distants',
          type: 'textarea',
        },
        {
          id: 'procedure_acces_equipe_soutien',
          label: 'Voulez-vous élaborer une procédure d\'accès pour les équipes de soutien externe afin de s\'assurer de ne pas divulguer d\'information à des personnes non autorisées ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'ISO 27001 (A.9.1.2) et NIST CSF 2.0 (PR.AC-4) exigent la gestion des accès tiers. ITIL 4 (Supplier Management) recommande des accords clairs avec les fournisseurs externes. Les procédures d\'accès pour les équipes de soutien sont essentielles pour maintenir la sécurité et la conformité.',
        },
        {
          id: 'notes_acces_equipe_soutien',
          label: 'Notes - Accès équipe de soutien',
          type: 'textarea',
        },
        {
          id: 'procedure_acces_employe_absent',
          label: 'Voulez-vous inclure une section qui explique la procédure à suivre dans le cas où un(e) employé serait absent et quelqu\'un devrait avoir accès à un dossier appartenant à cet employé ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'ITIL 4 (Access Management) et COBIT 2019 (DSS05.04) recommandent des procédures pour la gestion des accès en cas d\'absence. Cette pratique assure la continuité des opérations tout en maintenant la traçabilité et la sécurité.',
        },
        {
          id: 'notes_acces_employe_absent',
          label: 'Notes - Accès employé absent',
          type: 'textarea',
        },
        {
          id: 'longueur_mots_passe',
          label: 'Longueur des mots de passe',
          type: 'text',
          placeholder: 'Ex: 12 caractères minimum, 16 recommandé...',
          helpText: 'NIST SP 800-63B recommande un minimum de 12 caractères (8 caractères si combiné avec MFA). ISO 27001 (A.9.4.3) exige une longueur minimale appropriée. Les organisations modernes tendent vers 14-16 caractères minimum.',
        },
        {
          id: 'duree_vie_maximale_mots_passe',
          label: 'Durée de vie maximale des mots de passe',
          type: 'text',
          placeholder: 'Ex: 90 jours, 180 jours, 1 an...',
          helpText: 'NIST SP 800-63B recommande de ne PAS forcer la rotation périodique si MFA est en place. ISO 27001 (A.9.4.3) suggère une rotation selon le niveau de risque. La tendance moderne est vers moins de rotations (90-180 jours) ou aucune si MFA est activée.',
        },
        {
          id: 'interdiction_reutilisation_mots_passe',
          label: 'Interdiction de réutiliser un mot de passe à plus de 50%',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'NIST SP 800-63B et ISO 27001 (A.9.4.3) recommandent de prévenir la réutilisation des mots de passe récents. L\'historique de 12-24 mots de passe précédents est une pratique courante. La vérification à 50% signifie qu\'au moins 50% des caractères doivent être différents.',
        },
        {
          id: 'mot_passe_different_par_actif',
          label: 'Mot de passe différent pour chaque actif TI',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'NIST SP 800-63B et ISO 27001 (A.9.4.3) recommandent des mots de passe uniques par système. Cette pratique réduit l\'impact d\'une compromission. Les gestionnaires de mots de passe facilitent cette exigence.',
        },
        {
          id: 'notes_format_mots_passe',
          label: 'Notes - Format des mots de passe',
          type: 'textarea',
        },
        {
          id: 'utilisateurs_modifient_mots_passe',
          label: 'Permettez-vous aux utilisateurs de modifier eux-mêmes leurs mots de passe ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'ITIL 4 (Self-Service) et NIST SP 800-63B recommandent de permettre aux utilisateurs de gérer leurs propres mots de passe via un portail sécurisé. Cela réduit la charge sur le support TI et améliore l\'expérience utilisateur.',
        },
        {
          id: 'responsable_modification_mots_passe',
          label: 'Sinon, qui est responsable de la modification de ces mots de passe ?',
          type: 'select',
          options: ['BZ', 'Équipe TI interne', 'Service TI externe', 'Autre'],
          helpText: 'Si l\'auto-gestion n\'est pas permise, un processus clair doit être défini. ITIL 4 (Request Fulfillment) recommande un workflow structuré pour les demandes de réinitialisation de mots de passe.',
        },
        {
          id: 'notes_modification_mots_passe',
          label: 'Notes - Procédure de modification des mots de passe',
          type: 'textarea',
        },
        {
          id: 'noter_mots_passe_appareil_personnel',
          label: 'Permettez-vous aux utilisateurs de noter leurs mots de passe sur un appareil leur appartenant ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'NIST SP 800-63B et ISO 27001 (A.9.4.3) déconseillent fortement la notation de mots de passe, même sur appareils personnels. Les gestionnaires de mots de passe sécurisés sont la solution recommandée.',
        },
        {
          id: 'noter_mots_passe_papier',
          label: 'Sur un papier ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'La notation sur papier est fortement déconseillée par NIST SP 800-63B et ISO 27001 (A.9.4.3) car elle expose les mots de passe au vol, à la perte et à la visualisation non autorisée. Les gestionnaires de mots de passe sont la seule méthode acceptable.',
        },
        {
          id: 'noter_mots_passe_logiciel',
          label: 'Dans un logiciel spécialement conçu à cet effet ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'NIST SP 800-63B et ISO 27001 (A.9.4.3) recommandent l\'utilisation de gestionnaires de mots de passe approuvés (ex: 1Password, LastPass Enterprise, Bitwarden). Ces solutions offrent un stockage sécurisé et chiffré.',
        },
        {
          id: 'suggestion_gestionnaire_mots_passe',
          label: 'Suggestion d\'un gestionnaire de mots de passe',
          type: 'select',
          options: ['Acceptée', 'Refusée'],
          helpText: 'Les organisations devraient recommander ou fournir un gestionnaire de mots de passe approuvé. Cela facilite la conformité et réduit les risques. Les solutions d\'entreprise offrent des fonctionnalités de gestion centralisée et d\'audit.',
        },
        {
          id: 'notes_stockage_mots_passe',
          label: 'Notes - Stockage et utilisation des mots de passe',
          type: 'textarea',
        },
        {
          id: 'procedure_oubli_appareil_2fa',
          label: 'En cas d\'oubli de l\'appareil permettant d\'accepter la demande de 2FA, quelle serait la procédure à suivre ?',
          type: 'select',
          options: ['Prêt d\'un appareil', 'Code d\'accès temporaire', 'Désactivation temporaire du 2FA'],
          helpText: 'NIST SP 800-63B recommande des procédures de récupération sécurisées pour l\'authentification multi-facteurs. ITIL 4 (Access Management) suggère des processus clairs pour gérer les situations d\'exception tout en maintenant la sécurité.',
        },
        {
          id: 'notes_oubli_appareil_2fa',
          label: 'Notes - Authentification à deux facteurs (oubli appareil)',
          type: 'textarea',
        },
        {
          id: '2fa_utilisateurs_externes',
          label: 'Est-ce qu\'un utilisateur externe serait dans l\'obligation d\'utiliser une méthode de double authentification ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'NIST CSF 2.0 (PR.AC-7) et ISO 27001 (A.9.4.2) recommandent MFA pour tous les accès distants et les utilisateurs externes. COBIT 2019 (DSS05.04) considère MFA comme essentiel pour les accès tiers.',
        },
        {
          id: 'notes_utilisateurs_externes',
          label: 'Notes - Utilisateurs externes',
          type: 'textarea',
        },
        {
          id: 'methodes_2fa_autorisees',
          label: 'Quelles méthodes d\'authentification à deux facteurs sont autorisées ?',
          type: 'textarea',
          placeholder: 'Ex: Authentificateur Microsoft, Google Authenticator, SMS, Appels téléphoniques, Clés de sécurité physique (YubiKey, FIDO2)...',
          helpText: 'NIST SP 800-63B classe les méthodes MFA : niveau 1 (SMS/appels - moins sécurisé), niveau 2 (applications d\'authentification), niveau 3 (clés de sécurité matérielles - plus sécurisé). ISO 27001 (A.9.4.2) recommande des méthodes résistantes au phishing.',
        },
      ],
    },
    {
      id: 'phase4',
      title: 'Politiques & Restrictions',
      description: 'Courriel, transmission de données, verrouillage, médias externes, réseaux et IA',
      icon: 'FileText',
      questions: [
        {
          id: 'envoi_pieces_jointes_courriel',
          label: 'Autorisez-vous l\'envoi de pièces jointes par courriels ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'NIST CSF 2.0 (PR.DS-1) et ISO 27001 (A.13.2.1) recommandent des contrôles sur les pièces jointes pour prévenir les menaces (malware, phishing). Les solutions de sécurité email peuvent scanner et bloquer les pièces jointes suspectes.',
        },
        {
          id: 'envoi_liens_courriel',
          label: 'Autorisez-vous tout types de liens à être envoyés par courriel ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'Les liens dans les courriels sont un vecteur d\'attaque majeur (phishing, malware). NIST CSF 2.0 (PR.AT-5) recommande la sensibilisation et des contrôles techniques pour détecter les liens malveillants.',
        },
        {
          id: 'liens_plateformes_securisees_courriel',
          label: 'Autorisez-vous uniquement des liens menant vers des plateformes sécurisées (comme SharePoint) ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'Restreindre les liens aux plateformes approuvées (SharePoint, Teams, OneDrive) réduit les risques de phishing. NIST CSF 2.0 (PR.DS-1) recommande des contrôles sur les communications sortantes.',
        },
        {
          id: 'notes_courrier_electronique',
          label: 'Notes - Utilisation du courrier électronique',
          type: 'textarea',
        },
        {
          id: 'interdire_impression_donnees_sensibles',
          label: 'Voulez-vous interdire l\'impression de données sensibles ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'ISO 27001 (A.8.2.1) et NIST CSF 2.0 (PR.DS-1) recommandent des contrôles sur l\'impression pour prévenir la perte de données. Les solutions DLP peuvent bloquer l\'impression de documents classifiés.',
        },
        {
          id: 'interdire_partage_comptes_non_entreprise',
          label: 'Voulez-vous interdire tout partage d\'information professionnelle vers des comptes ou des équipements n\'appartenant pas à l\'entreprise ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'NIST CSF 2.0 (PR.DS-1) et ISO 27001 (A.8.2.1) exigent des contrôles pour prévenir la perte de données. Les solutions DLP peuvent détecter et bloquer les partages non autorisés vers des comptes personnels (Gmail, Dropbox, etc.).',
        },
        {
          id: 'section_backup_onedrive_teams_sharepoint',
          label: 'Voulez-vous ajouter une section qui explique aux employés qu\'ils doivent travailler sur leurs fichiers dans OneDrive/Teams/SharePoint afin d\'avoir un backup de leurs données ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'ITIL 4 (Service Continuity Management) et NIST CSF 2.0 (RC.RP-1) recommandent des stratégies de sauvegarde. Travailler directement dans des plateformes cloud (OneDrive, Teams, SharePoint) assure la sauvegarde automatique et la disponibilité des données.',
        },
        {
          id: 'notes_restrictions_transmission',
          label: 'Notes - Restrictions sur la transmission et le stockage hors site',
          type: 'textarea',
        },
        {
          id: 'verrouillage_session_systematique',
          label: 'Est-ce que les utilisateurs devraient verrouiller leur session systématiquement lorsqu\'ils quittent leur poste de travail ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'ISO 27001 (A.11.2.6) et NIST CSF 2.0 (PR.AC-1) exigent le verrouillage des sessions. Cette pratique prévient l\'accès non autorisé aux postes de travail laissés sans surveillance.',
        },
        {
          id: 'fermeture_ecran_minutes_inactivite',
          label: 'La fermeture de l\'écran se fait automatiquement après combien de minutes d\'inactivité ?',
          type: 'text',
          placeholder: 'Ex: 5 minutes, 10 minutes, 15 minutes...',
          helpText: 'NIST CSF 2.0 (PR.AC-1) et ISO 27001 (A.11.2.6) recommandent un délai d\'inactivité approprié. Les valeurs courantes sont 5-15 minutes selon le niveau de sensibilité des données. Les environnements sensibles utilisent des délais plus courts.',
        },
        {
          id: 'verrouillage_automatique_minutes_inactivite',
          label: 'Le verrouillage automatique de la session se fait après combien de minutes d\'inactivité ?',
          type: 'text',
          placeholder: 'Ex: 10 minutes, 15 minutes, 30 minutes...',
          helpText: 'Le verrouillage automatique devrait être configuré à un délai égal ou légèrement supérieur à la fermeture d\'écran. NIST CSF 2.0 (PR.AC-1) recommande des délais cohérents avec la criticité des données.',
        },
        {
          id: 'notes_verrouillage_sessions',
          label: 'Notes - Verrouillage des sessions',
          type: 'textarea',
        },
        {
          id: 'utilisation_medias_numeriques_externes',
          label: 'Permettez-vous l\'utilisation des médias numériques externes, tels que les clés USB et les disques durs externes ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'NIST CSF 2.0 (PR.DS-1) et ISO 27001 (A.8.2.1) recommandent des contrôles stricts sur les médias amovibles. Ces appareils sont des vecteurs de malware et de perte de données. Les solutions DLP et de contrôle des périphériques peuvent bloquer ou monitorer leur utilisation.',
        },
        {
          id: 'connexion_reseaux_externes',
          label: 'Permettez-vous aux utilisateurs de se connecter à tous les réseaux externes à l\'organisation ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'NIST CSF 2.0 (PR.DS-1) et ISO 27001 (A.13.1.1) recommandent des contrôles sur les connexions réseau. Les réseaux publics non sécurisés (WiFi public) présentent des risques de man-in-the-middle et d\'interception de données.',
        },
        {
          id: 'reseaux_externes_autorises',
          label: 'Sinon, lesquels sont autorisés ?',
          type: 'textarea',
          placeholder: 'Ex: Réseaux WiFi sécurisés avec VPN, réseaux d\'hôtels avec authentification, réseaux d\'institutions partenaires...',
          helpText: 'Les connexions à des réseaux externes devraient être sécurisées via VPN. NIST CSF 2.0 (PR.DS-1) recommande le chiffrement des communications sur les réseaux non fiables.',
        },
        {
          id: 'methode_connexion_reseaux_externes',
          label: 'Méthode de mise en place - Connexion à des réseaux externes',
          type: 'select',
          options: ['Confiance envers les employés', 'Restriction technologique'],
        },
        {
          id: 'moyens_restriction_reseaux_externes',
          label: 'Quels moyens de restriction technologique allez-vous utiliser ? - Connexion à des réseaux externes',
          type: 'textarea',
          placeholder: 'Ex: VPN obligatoire, solutions de sécurité réseau, monitoring des connexions, blocage des réseaux non autorisés...',
          helpText: 'Les solutions VPN et de sécurité réseau permettent de contrôler et sécuriser les connexions externes. NIST CSF 2.0 (PR.DS-1) recommande des contrôles techniques pour protéger les données en transit.',
        },
        {
          id: 'notes_reseaux_externes',
          label: 'Notes - Utilisation du matériel informatique',
          type: 'textarea',
        },
        {
          id: 'licences_comptes_professionnels_ia',
          label: 'Avez-vous des licences pour des comptes professionnels d\'application d\'intelligence artificielle ?',
          type: 'select',
          options: ['Oui', 'Non'],
          helpText: 'L\'utilisation de l\'IA en entreprise nécessite des politiques claires pour gérer les risques de confidentialité, de sécurité des données et de conformité. Les licences professionnelles offrent généralement de meilleures garanties de protection des données que les versions gratuites.',
        },
        {
          id: 'empecher_utilisation_applications_ia',
          label: 'Empêchez-vous l\'utilisation de certaines applications d\'intelligence artificielle ?',
          type: 'text',
          placeholder: 'Ex: ChatGPT gratuit, applications IA non approuvées...',
          helpText: 'Les organisations devraient maintenir une liste d\'applications IA approuvées et interdites. Les versions gratuites peuvent exposer les données à des risques de confidentialité. NIST CSF 2.0 (PR.DS-1) recommande des contrôles sur l\'utilisation des services cloud et SaaS.',
        },
      ],
    },
  ],
};

